import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {SortOrder, SubjectSortField} from 'gql/types';
import {addToast} from '@albert-io/atomic';
import {normalizeError} from 'lib/normalizeError';

import type {
  SubjectLibrarySubjectsQuery,
  SubjectLibrarySubjectsQueryVariables
} from './SubjectMenuRenderer.queries.generated';
import {
  useSubjectLibrarySubjectsQuery,
  useSubjectLibrarySubjectsLazyQuery
} from './SubjectMenuRenderer.queries.generated';

type GQLSubjectResult = NonNullable<
  NonNullable<SubjectLibrarySubjectsQuery['subjects']>['results']
>[number];

const PAGE_SIZE = 250;

const NAME_ASC_SORT: SubjectLibrarySubjectsQueryVariables['sort'] = [
  {field: SubjectSortField.Name, order: SortOrder.Asc}
];

interface UseSubjectLibrarySubjectsPaginatedOptions {
  filter: Record<string, any>;
  searchString?: string;
  skip?: boolean;
}

interface UseSubjectLibrarySubjectsPaginatedResult {
  subjects: GQLSubjectResult[];
  loading: boolean;
  loadingMore: boolean;
  error: ReturnType<typeof useSubjectLibrarySubjectsQuery>['error'];
  hasNextPage: boolean;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Wraps the generated `useSubjectLibrarySubjectsQuery` with keyset pagination
 * and lazy-loading. Returns all subjects accumulated across pages, sorted
 * alphabetically (NAME ASC) server-side, or by search relevance when a
 * search string is provided.
 *
 * Attach `sentinelRef` to a DOM element at the bottom of the subject list;
 * when it becomes visible, the next page is automatically fetched.
 */
export function useSubjectLibrarySubjectsPaginated({
  filter,
  searchString = '',
  skip = false
}: UseSubjectLibrarySubjectsPaginatedOptions): UseSubjectLibrarySubjectsPaginatedResult {
  const [additionalSubjects, setAdditionalSubjects] = useState<GQLSubjectResult[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);

  const endKeysetRef = useRef<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement | null>;
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Counter to detect stale fetchMore responses when filter changes
  const filterGenerationRef = useRef(0);

  const filterJson = JSON.stringify(filter);

  const sort = useMemo<SubjectLibrarySubjectsQueryVariables['sort']>(() => {
    if (searchString) {
      return [
        {
          field: SubjectSortField.SearchRelevance,
          order: SortOrder.Desc,
          searchRelevanceInput: {search: searchString, includeDescription: true}
        }
      ];
    }
    return NAME_ASC_SORT;
  }, [searchString]);

  // Initial page query
  const {data, loading, error} = useSubjectLibrarySubjectsQuery({
    variables: {filter, first: PAGE_SIZE, sort},
    skip
  });

  // Reset pagination state when filter or initial data changes
  useEffect(() => {
    filterGenerationRef.current += 1;
    // oxlint-disable-next-line react/set-state-in-effect -- pagination reset entangled with Apollo data arrival and the generation ref
    setAdditionalSubjects([]);
    setLoadingMore(false);

    if (data?.subjects) {
      const results = data.subjects.results ?? [];
      endKeysetRef.current = data.subjects.endKeyset ?? null;
      setHasNextPage(results.length === PAGE_SIZE && !!data.subjects.endKeyset);
    } else {
      endKeysetRef.current = null;
      setHasNextPage(false);
    }
  }, [data, filterJson]);

  // Lazy query for subsequent pages
  const [fetchNextPage] = useSubjectLibrarySubjectsLazyQuery();

  const loadMore = useCallback(async () => {
    if (!hasNextPage || loadingMore || !endKeysetRef.current) return;

    const generation = filterGenerationRef.current;
    setLoadingMore(true);

    try {
      const result = await fetchNextPage({
        variables: {filter, first: PAGE_SIZE, after: endKeysetRef.current, sort}
      });

      // Discard if filter changed while loading
      if (generation !== filterGenerationRef.current) return;

      const newResults = result.data?.subjects?.results ?? [];
      const newEndKeyset = result.data?.subjects?.endKeyset ?? null;

      endKeysetRef.current = newEndKeyset;
      setAdditionalSubjects((prev) => [...prev, ...newResults]);
      setHasNextPage(newResults.length === PAGE_SIZE && !!newEndKeyset);
    } catch (err) {
      if (generation !== filterGenerationRef.current) return;
      const appError = normalizeError(err);
      addToast({
        color: 'negative',
        title: 'Something went wrong',
        message: appError.message
      });
    } finally {
      if (generation === filterGenerationRef.current) {
        setLoadingMore(false);
      }
    }
  }, [hasNextPage, loadingMore, filter, sort, fetchNextPage]);

  // IntersectionObserver on the sentinel element to trigger lazy loading
  useEffect(() => {
    observerRef.current?.disconnect();

    if (!sentinelRef.current || !hasNextPage) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      {threshold: 0}
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [hasNextPage, loadMore]);

  // Combine initial + additional subjects
  const subjects: GQLSubjectResult[] = [...(data?.subjects?.results ?? []), ...additionalSubjects];

  return {
    subjects,
    loading,
    loadingMore,
    error,
    hasNextPage,
    sentinelRef
  };
}
